// App-wide shell state: which application is active, the lead-drawer target,
// the "call this lead next" target, and cosmetic preferences (accent/density/zebra).
// Lives inside the authenticated layout so its queries always run with a session.

import type { AppWithPlans } from '@delvero/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { orpc } from './lib/orpc';

const ACTIVE_APP_KEY = 'delvero-active-app';
const PREFS_KEY = 'delvero-prefs-v1';

export type Density = 'kompaktowa' | 'wygodna';
export interface Prefs {
  accent: string;
  density: Density;
  zebra: boolean;
}
export const ACCENT_OPTIONS = ['#2ED393', '#5E9DFF', '#9B8CFF', '#F5A524'];
const DEFAULT_PREFS: Prefs = { accent: '#2ED393', density: 'kompaktowa', zebra: false };

function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) return { ...DEFAULT_PREFS, ...(JSON.parse(raw) as Partial<Prefs>) };
  } catch {
    /* ignore */
  }
  return DEFAULT_PREFS;
}

interface ShellValue {
  apps: AppWithPlans[];
  appsLoading: boolean;
  activeAppId: string | null;
  activeApp: AppWithPlans | null;
  setActiveAppId: (id: string) => void;

  openLeadId: string | null;
  openLead: (id: string) => void;
  closeLead: () => void;

  callTarget: string | null;
  setCallTarget: (id: string | null) => void;

  prefs: Prefs;
  updatePrefs: (patch: Partial<Prefs>) => void;
}

const ShellCtx = createContext<ShellValue | null>(null);

export function useShell(): ShellValue {
  const ctx = useContext(ShellCtx);
  if (!ctx) throw new Error('useShell must be used within <ShellProvider>');
  return ctx;
}

/** Create a new application, refresh the list, and make it active. Shared by the
 * app switcher and the empty-state. Must be used within <ShellProvider>. */
export function useCreateApp() {
  const queryClient = useQueryClient();
  const { setActiveAppId } = useShell();
  return useMutation(
    orpc.apps.create.mutationOptions({
      onSuccess: (app) => {
        void queryClient.invalidateQueries({ queryKey: orpc.apps.key() });
        setActiveAppId(app.id);
      },
    }),
  );
}

export function ShellProvider({ children }: { children: ReactNode }) {
  const appsQuery = useQuery(orpc.apps.list.queryOptions());
  const apps = appsQuery.data ?? [];

  const [storedAppId, setStoredAppId] = useState<string | null>(() => localStorage.getItem(ACTIVE_APP_KEY));
  const [openLeadId, setOpenLeadId] = useState<string | null>(null);
  const [callTarget, setCallTarget] = useState<string | null>(null);
  const [prefs, setPrefs] = useState<Prefs>(loadPrefs);

  const activeAppId = storedAppId && apps.some((a) => a.id === storedAppId) ? storedAppId : (apps[0]?.id ?? null);
  const activeApp = apps.find((a) => a.id === activeAppId) ?? null;

  const setActiveAppId = useCallback((id: string) => {
    setStoredAppId(id);
    try {
      localStorage.setItem(ACTIVE_APP_KEY, id);
    } catch {
      /* ignore */
    }
  }, []);

  const updatePrefs = useCallback((patch: Partial<Prefs>) => {
    setPrefs((prev) => {
      const next = { ...prev, ...patch };
      try {
        localStorage.setItem(PREFS_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const value: ShellValue = {
    apps,
    appsLoading: appsQuery.isLoading,
    activeAppId,
    activeApp,
    setActiveAppId,
    openLeadId,
    openLead: useCallback((id: string) => setOpenLeadId(id), []),
    closeLead: useCallback(() => setOpenLeadId(null), []),
    callTarget,
    setCallTarget,
    prefs,
    updatePrefs,
  };

  return <ShellCtx.Provider value={value}>{children}</ShellCtx.Provider>;
}
